import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';

const QuickScanViewModal = ({ pet, onClose, onLocationUpdated, onKeepsakesClick }) => {
  const [selectedLocation, setSelectedLocation] = useState(pet.location_area || '');
  const [staffName, setStaffName] = useState('');
  const [updating, setUpdating] = useState(false);

  const locations = [
    'Not Yet in Our Care',
    'Refrigeration',
    'Cremation Chambers',
    'Cooling / Ashes Processing',
    'Urn Assembly',
    'Ready for Collection'
  ];

  const handleUpdateLocation = async () => {
    if (!selectedLocation) {
      showToast('Please select a location', 'error');
      return;
    }

    console.log('🔄 Updating location for pet:', pet.pet_name, pet.id);
    console.log('New location:', selectedLocation);
    console.log('Staff name:', staffName || 'Not provided');

    // Check if moving to Cremators and has uncollected keepsakes
    if (selectedLocation === 'Cremation Chambers' && pet.keepsakes_selected && pet.keepsakes_selected.length > 0) {
      const keepsakes_collection = pet.keepsakes_collection || [];
      const collected = keepsakes_collection.filter(k => k.collected).length;
      const total = pet.keepsakes_selected.length;

      if (collected < total) {
        const missingItems = pet.keepsakes_selected.filter(k => !keepsakes_collection.find(kc => kc.keepsake_name === k && kc.collected));
        const confirmed = window.confirm(
          `⚠️ Keepsakes Not Complete\n\n${collected} of ${total} keepsakes collected.\n\nMissing items:\n${missingItems.join('\n- ')}\n\nContinue to Cremators anyway?`
        );
        if (!confirmed) {
          console.log('❌ User cancelled - keepsakes incomplete');
          return;
        }
      }
    }

    setUpdating(true);
    try {
      const url = `${API}/pets/${pet.id}/update-location`;
      console.log('Calling API:', url);
      
      const response = await axios.post(url, null, {
        params: {
          location_area: selectedLocation,
          location_detail: '',
          staff_name: staffName || ''
        }
      });
      
      console.log('✅ Location updated successfully:', response.data);
      showToast('✅ Location updated successfully!');
      onLocationUpdated();
    } catch (error) {
      console.error('❌ Error updating location:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Failed to update location: ${errorMsg}`, 'error');
    } finally {
      setUpdating(false);
    }
  };

  // Calculate keepsakes status
  const keepsakes_collection = pet.keepsakes_collection || [];
  const collectedCount = keepsakes_collection.filter(k => k.collected).length;
  const totalKeepsakes = pet.keepsakes_selected ? pet.keepsakes_selected.length : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Pet Information</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* Pet Info Summary */}
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <h4 className="font-bold text-lg text-gray-900 mb-2">{pet.pet_name}</h4>
          <p className="text-sm text-gray-600">Client: {pet.client_name}</p>
          <p className="text-sm text-gray-600">Weight: {pet.weight_kg} kg</p>
          <p className="text-sm text-gray-600">Current Location: <span className="font-semibold">{pet.location_area}</span></p>
          
          {totalKeepsakes > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700">
                Keepsakes: {collectedCount} of {totalKeepsakes} collected
              </p>
              {collectedCount < totalKeepsakes && (
                <button
                  onClick={onKeepsakesClick}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  📦 View & Collect Keepsakes →
                </button>
              )}
            </div>
          )}
        </div>

        {/* Location Update */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Update Location
            </label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select new location...</option>
              {locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Staff Name (optional)
            </label>
            <input
              type="text"
              value={staffName}
              onChange={(e) => setStaffName(e.target.value)}
              placeholder="Enter your name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleUpdateLocation}
              disabled={updating || !selectedLocation}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium"
            >
              {updating ? 'Updating...' : '✅ Confirm Location'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>

          <button
            onClick={() => {
              window.open(`/pets/${pet.id}`, '_blank');
            }}
            className="w-full text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            👁️ View Full Details
          </button>
        </div>
      </div>
    </div>
  );
};

// =======================
// KEEPSAKES COLLECTION MODAL COMPONENT
// =======================

export default QuickScanViewModal;
